/** Conflict panel — surfaces detector output with severity color coding. */
import type { ConflictReport } from "@/entities/mission-control";

const TONE: Record<ConflictReport["severity"], string> = {
  info:    "bg-sky-100 text-sky-900 dark:bg-sky-900/40 dark:text-sky-200",
  warning: "bg-amber-100 text-amber-900 dark:bg-amber-900/40 dark:text-amber-200",
  error:   "bg-rose-100 text-rose-900 dark:bg-rose-900/40 dark:text-rose-200",
};

const TYPE_LABEL: Record<ConflictReport["type"], string> = {
  duplicate_time_slot:       "Duplicate slot",
  channel_congestion:        "Channel congestion",
  campaign_overlap:          "Campaign overlap",
  missing_approval:          "Missing approval",
  missing_media:             "Missing media",
  missing_required_metadata: "Missing metadata",
};

export function ConflictPanel({ conflicts }: { conflicts: ConflictReport[] }): JSX.Element {
  return (
    <div
      data-testid="mc-conflicts-panel"
      className="rounded-lg border border-border/60 bg-card p-3 shadow-sm"
    >
      <div className="mb-2 flex items-center justify-between">
        <div className="text-sm font-semibold">Conflicts</div>
        <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
          {conflicts.length} detected
        </span>
      </div>
      {conflicts.length === 0 ? (
        <div className="rounded-md border border-dashed border-border/40 px-3 py-4 text-center text-[11px] text-muted-foreground">
          All clear — no scheduling conflicts.
        </div>
      ) : (
        <ul className="space-y-2">
          {conflicts.slice(0, 10).map((c) => (
            <li
              key={c.id}
              data-testid={`mc-conflict-${c.id}`}
              className="flex items-start gap-2 rounded-md border border-border/40 p-2"
            >
              <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[9px] font-medium uppercase tracking-wide ${TONE[c.severity]}`}>
                {TYPE_LABEL[c.type]}
              </span>
              <div className="flex-1 text-[11px] leading-snug text-foreground">
                {c.message}
                <div className="mt-0.5 font-mono text-[9px] uppercase tracking-wider text-muted-foreground">
                  {c.asset_ids.length} asset{c.asset_ids.length === 1 ? "" : "s"}
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
